import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { ApiError, api } from "../lib/api.ts";
import { useI18n } from "../lib/i18n.tsx";
import { useMe } from "../lib/me.tsx";

/** /p/:id/invite — buyer's one-tap request (R-INV-4). */
export function InviteAsk() {
  const { id = "" } = useParams();
  const { t } = useI18n();
  const { me, loading } = useMe();
  const navigate = useNavigate();
  const [state, setState] = useState<"idle" | "sent" | "error">("idle");
  const [msg, setMsg] = useState<string | null>(null);
  if (loading) return <div className="page muted">{t("common.loading")}</div>;
  if (!me) return <Navigate to={`/login?next=/p/${id}/invite`} replace />;
  async function ask() {
    try {
      await api(`/api/purchases/${id}/invite-request`, { body: {} });
      setState("sent");
    } catch (e) {
      setState("error");
      setMsg(
        e instanceof ApiError && e.body.error === "already_requested"
          ? t("invite.ask.sent")
          : e instanceof ApiError && e.body.error === "already_seller"
            ? t("settings.seller.status", { n: "" })
            : t("error.generic"),
      );
    }
  }
  return (
    <div className="page page--narrow">
      <h1 className="h1">{t("invite.ask.cta")}</h1>
      {state === "sent" ? (
        <p className="ok" data-testid="invite-sent">
          {t("invite.ask.sent")}
        </p>
      ) : null}
      {msg ? <p className="muted">{msg}</p> : null}
      {state === "idle" ? (
        <button className="btn btn--accent" onClick={() => void ask()} data-testid="invite-ask">
          {t("invite.ask.cta")}
        </button>
      ) : null}
      <p>
        <button className="btn btn--ghost" onClick={() => navigate(`/p/${id}`)}>
          {t("common.back")}
        </button>
      </p>
    </div>
  );
}

type Req = { id: string; status: string; created_at: number; buyer_handle: string; title: string };

/** /me/invite-requests — seller accepts (consumes a code) or declines. */
export function InviteRequests() {
  const { t } = useI18n();
  const { me, loading } = useMe();
  const [items, setItems] = useState<Req[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!me) return;
    let live = true;
    api<{ items: Req[] }>("/api/me/invite-requests")
      .then((r) => live && setItems(r.items))
      .catch(() => live && setItems([]));
    return () => {
      live = false;
    };
  }, [me]);
  if (loading) return <div className="page muted">{t("common.loading")}</div>;
  if (!me) return <Navigate to="/login?next=/me/invite-requests" replace />;
  async function respond(id: string, accept: boolean) {
    setError(null);
    try {
      const r = await api<{ status: string }>(`/api/invite-requests/${id}/respond`, {
        body: { accept },
      });
      setItems((list) => list?.map((x) => (x.id === id ? { ...x, status: r.status } : x)) ?? null);
    } catch (e) {
      setError(
        e instanceof ApiError && e.body.error === "no_codes"
          ? t("settings.invite.topup")
          : t("error.generic"),
      );
    }
  }
  return (
    <div className="page page--narrow">
      <h1 className="h1">
        {t("notif.invite_requested.title", { buyer: "…" }).replace("@…", "").trim()}
      </h1>
      {error ? <p className="error">{error}</p> : null}
      <ul className="list" data-testid="invite-requests">
        {items?.map((r) => (
          <li key={r.id} className="list__row">
            <div>
              <div>
                @{r.buyer_handle} · {r.title}
              </div>
              <div className="hint">{r.status}</div>
            </div>
            {r.status === "pending" ? (
              <div className="row">
                <button
                  className="btn btn--accent"
                  onClick={() => void respond(r.id, true)}
                  data-testid="invite-accept"
                >
                  {t("common.yes")}
                </button>
                <button className="btn btn--ghost" onClick={() => void respond(r.id, false)}>
                  {t("common.no")}
                </button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
